import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import {
  XMarkIcon,
  SwatchIcon,
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentCheckIcon,
  ShoppingBagIcon,
  SparklesIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  EnvelopeIcon,
  AtSymbolIcon,
  KeyIcon,
  ServerIcon,
  ShieldCheckIcon,
  PaperAirplaneIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import {
  obtenerConfiguracionTienda,
  guardarConfiguracionTienda,
  probarEmailConfiguracionTienda
} from '../api/backend_v2';
import { getStorefrontUrl } from '../utils/storefront';

const PALETAS_PREDEFINIDAS = [
  {
    nombre: 'Oscuro Óptico (Por defecto)',
    color_fondo_principal: '#020617',
    color_fondo_tarjetas: '#0f172a',
    color_botones: '#0891b2',
    color_texto_botones: '#ffffff'
  },
  {
    nombre: 'Azul Noche & Real',
    color_fondo_principal: '#0b1120',
    color_fondo_tarjetas: '#1e293b',
    color_botones: '#2563eb',
    color_texto_botones: '#ffffff'
  },
  {
    nombre: 'Grafito & Esmeralda',
    color_fondo_principal: '#18181b',
    color_fondo_tarjetas: '#27272a',
    color_botones: '#059669',
    color_texto_botones: '#ffffff'
  },
  {
    nombre: 'Carbón & Violeta',
    color_fondo_principal: '#090d16',
    color_fondo_tarjetas: '#171923',
    color_botones: '#7c3aed',
    color_texto_botones: '#ffffff'
  },
  {
    nombre: 'Obsidiana & Ámbar',
    color_fondo_principal: '#0c0a09',
    color_fondo_tarjetas: '#1c1917',
    color_botones: '#d97706',
    color_texto_botones: '#ffffff'
  },
  {
    nombre: 'Marino Profundo & Turquesa',
    color_fondo_principal: '#042f2e',
    color_fondo_tarjetas: '#134e4a',
    color_botones: '#14b8a6',
    color_texto_botones: '#042f2e'
  }
];

export default function TiendaDisenoModal({
  isOpen,
  onClose,
  tiendaData,
  storefrontUrl,
  onSaved
}) {
  const [tabActiva, setTabActiva] = useState('diseno'); // 'diseno' | 'email'
  const [form, setForm] = useState({
    color_fondo_principal: '#020617',
    color_fondo_tarjetas: '#0f172a',
    color_botones: '#0891b2',
    color_texto_botones: '#ffffff',
    subtitulo_hero:
      'Elegí tus armazones favoritos con descuento exclusivo en efectivo o transferencia. Retirá en nuestra sucursal o recibilo en tu domicilio.'
  });

  const [emailForm, setEmailForm] = useState({
    email_notificaciones: '',
    usar_smtp_propio: false,
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_user: '',
    smtp_pass: '',
    tiene_smtp_pass: false,
    smtp_plataforma_activo: false,
    smtp_plataforma_email: ''
  });

  const [infoOptica, setInfoOptica] = useState(tiendaData || null);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [probandoEmail, setProbandoEmail] = useState(false);
  const [emailDestinoPrueba, setEmailDestinoPrueba] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    setCargando(true);
    obtenerConfiguracionTienda()
      .then((data) => {
        if (data) {
          setInfoOptica(data);
          if (data.tienda_config) {
            setForm({
              color_fondo_principal: data.tienda_config.color_fondo_principal || '#020617',
              color_fondo_tarjetas: data.tienda_config.color_fondo_tarjetas || '#0f172a',
              color_botones: data.tienda_config.color_botones || '#0891b2',
              color_texto_botones: data.tienda_config.color_texto_botones || '#ffffff',
              subtitulo_hero:
                data.tienda_config.subtitulo_hero ||
                'Elegí tus armazones favoritos con descuento exclusivo en efectivo o transferencia. Retirá en nuestra sucursal o recibilo en tu domicilio.'
            });
          }
          if (data.email_config) {
            setEmailForm({
              email_notificaciones: data.email_config.email_notificaciones || data.email || '',
              usar_smtp_propio: Boolean(data.email_config.usar_smtp_propio),
              smtp_host: data.email_config.smtp_host || 'smtp.gmail.com',
              smtp_port: data.email_config.smtp_port || 587,
              smtp_user: data.email_config.smtp_user || '',
              smtp_pass: data.email_config.tiene_smtp_pass ? '••••••••' : '',
              tiene_smtp_pass: Boolean(data.email_config.tiene_smtp_pass),
              smtp_plataforma_activo: Boolean(data.email_config.smtp_plataforma_activo),
              smtp_plataforma_email: data.email_config.smtp_plataforma_email || ''
            });
            if (data.email_config.email_notificaciones || data.email) {
              setEmailDestinoPrueba(data.email_config.email_notificaciones || data.email || '');
            }
          }
        }
      })
      .catch((err) => {
        console.error('Error cargando configuración de tienda:', err);
      })
      .finally(() => {
        setCargando(false);
      });
  }, [isOpen]);

  if (!isOpen) return null;

  const slugOptica = infoOptica?.slug || tiendaData?.slug || '';
  const urlFinalTienda = storefrontUrl || getStorefrontUrl(slugOptica);

  const aplicarPaleta = (paleta) => {
    setForm((prev) => ({
      ...prev,
      color_fondo_principal: paleta.color_fondo_principal,
      color_fondo_tarjetas: paleta.color_fondo_tarjetas,
      color_botones: paleta.color_botones,
      color_texto_botones: paleta.color_texto_botones
    }));
  };

  const handleCopiarLink = () => {
    navigator.clipboard.writeText(urlFinalTienda);
    Swal.fire({
      icon: 'success',
      title: 'Enlace copiado',
      text: `Se copió ${urlFinalTienda} al portapapeles.`,
      timer: 1500,
      showConfirmButton: false
    });
  };

  const handleGuardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const payload = {
        ...form,
        email_config: {
          email_notificaciones: emailForm.email_notificaciones,
          usar_smtp_propio: emailForm.usar_smtp_propio,
          smtp_host: emailForm.smtp_host,
          smtp_port: emailForm.smtp_port,
          smtp_user: emailForm.smtp_user,
          smtp_pass: emailForm.smtp_pass
        }
      };
      const res = await guardarConfiguracionTienda(payload);
      if (onSaved) onSaved(res.tienda_config);
      Swal.fire({
        icon: 'success',
        title: 'Configuración Guardada',
        text: 'Los cambios visuales y de correo se actualizaron exitosamente.',
        timer: 1800,
        showConfirmButton: false
      });
      onClose();
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error al guardar configuración',
        text: err.message
      });
    } finally {
      setGuardando(false);
    }
  };

  const handleProbarEmail = async () => {
    const destino = emailDestinoPrueba || emailForm.email_notificaciones;
    if (!destino || !destino.includes('@')) {
      Swal.fire({
        icon: 'warning',
        title: 'Email requerido',
        text: 'Ingresá una dirección de correo válida para recibir el mensaje de prueba.'
      });
      return;
    }

    setProbandoEmail(true);
    try {
      const res = await probarEmailConfiguracionTienda({
        destinatario: destino,
        usar_smtp_propio: emailForm.usar_smtp_propio,
        smtp_host: emailForm.smtp_host,
        smtp_port: emailForm.smtp_port,
        smtp_user: emailForm.smtp_user,
        smtp_pass: emailForm.smtp_pass
      });
      Swal.fire({
        icon: 'success',
        title: 'Prueba Exitosa',
        text: res.mensaje || 'Email de prueba enviado exitosamente. Revisá tu bandeja de entrada.'
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Fallo de Envío SMTP',
        text: err.message || 'No se pudo conectar con el servidor de correo especificado.'
      });
    } finally {
      setProbandoEmail(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1050] flex items-center justify-center p-3 sm:p-4 overflow-y-auto bg-slate-950/80 backdrop-blur-sm overscroll-contain animate-fadeIn"
      onClick={(e) => {
        if (e.target === e.currentTarget && !guardando && !probandoEmail) {
          onClose();
        }
      }}
    >
      <div
        className="relative w-full max-w-4xl bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col my-auto max-h-[90vh] sm:max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-indigo-900 via-blue-900 to-slate-900 text-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-white/10">
              <SwatchIcon className="w-6 h-6 text-cyan-300" />
            </div>
            <div>
              <h3 className="text-base font-bold">
                Configuración de Tienda Online
              </h3>
              <p className="text-xs text-blue-200">
                Personalizá la vidriera digital, el enlace público y las notificaciones por correo
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Barra de Pestañas (Tabs) */}
        <div className="flex items-center border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 px-6 shrink-0">
          <button
            type="button"
            onClick={() => setTabActiva('diseno')}
            className={`flex items-center gap-2 py-3 px-4 border-b-2 font-semibold text-xs transition-colors ${
              tabActiva === 'diseno'
                ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <SwatchIcon className="w-4 h-4" />
            <span>Diseño & Vidriera</span>
          </button>
          <button
            type="button"
            onClick={() => setTabActiva('email')}
            className={`flex items-center gap-2 py-3 px-4 border-b-2 font-semibold text-xs transition-colors ${
              tabActiva === 'email'
                ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <EnvelopeIcon className="w-4 h-4" />
            <span>Notificaciones por Email</span>
          </button>
        </div>

        {cargando ? (
          <div className="p-12 flex flex-col items-center justify-center gap-3 text-slate-500 flex-1">
            <ArrowPathIcon className="w-8 h-8 animate-spin text-indigo-600" />
            <p className="text-sm font-medium">Cargando configuración...</p>
          </div>
        ) : (
          <form onSubmit={handleGuardar} className="flex flex-col flex-1 min-h-0 overflow-hidden">
            <div className="p-5 sm:p-6 space-y-6 overflow-y-auto overscroll-contain flex-1">
              
              {/* TAB 1: DISEÑO Y VIDRIERA */}
              {tabActiva === 'diseno' && (
                <div className="space-y-6 animate-fadeIn">
                  {/* Bloque de Enlace Directo a la Tienda */}
                  <div className="bg-slate-50 dark:bg-slate-900/70 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="space-y-1">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 block">
                        Link Directo a tu Tienda Online
                      </span>
                      <p className="text-xs text-slate-600 dark:text-slate-300 font-mono bg-white dark:bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 inline-block break-all">
                        {urlFinalTienda}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={handleCopiarLink}
                        className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600 text-xs font-bold transition-colors"
                      >
                        <ClipboardDocumentCheckIcon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                        <span>Copiar Link</span>
                      </button>
                      <a
                        href={urlFinalTienda}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold shadow-md transition-colors"
                      >
                        <span>Abrir Tienda</span>
                        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                      </a>
                    </div>
                  </div>

                  {/* Paletas Predefinidas */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <SparklesIcon className="w-4 h-4 text-indigo-500" />
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200">
                        Paletas de Colores Recomendadas
                      </h4>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {PALETAS_PREDEFINIDAS.map((paleta, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => aplicarPaleta(paleta)}
                          className="flex flex-col p-3 rounded-2xl border border-slate-200 dark:border-slate-700 hover:border-indigo-500 dark:hover:border-indigo-400 bg-slate-50 dark:bg-slate-800/60 transition-all text-left group"
                        >
                          <span className="text-xs font-bold text-slate-800 dark:text-slate-100 mb-2 group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                            {paleta.nombre}
                          </span>
                          <div className="flex items-center gap-1.5 mt-auto">
                            <span
                              className="w-5 h-5 rounded-full border border-white/20 shadow-sm"
                              style={{ backgroundColor: paleta.color_fondo_principal }}
                              title="Fondo Principal"
                            />
                            <span
                              className="w-5 h-5 rounded-full border border-white/20 shadow-sm"
                              style={{ backgroundColor: paleta.color_fondo_tarjetas }}
                              title="Fondo Tarjetas"
                            />
                            <span
                              className="w-5 h-5 rounded-full border border-white/20 shadow-sm"
                              style={{ backgroundColor: paleta.color_botones }}
                              title="Botón de Compra"
                            />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Configuración Manual de Colores */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 space-y-3">
                      <label className="text-xs font-bold text-slate-700 dark:text-slate-200 block">
                        Color de Fondo Principal (Vidriera)
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={form.color_fondo_principal}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, color_fondo_principal: e.target.value }))
                          }
                          className="w-10 h-10 rounded-xl cursor-pointer border-0 p-0 bg-transparent"
                        />
                        <input
                          type="text"
                          value={form.color_fondo_principal}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, color_fondo_principal: e.target.value }))
                          }
                          className="flex-1 font-mono text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                        />
                      </div>
                    </div>

                    <div className="p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 space-y-3">
                      <label className="text-xs font-bold text-slate-700 dark:text-slate-200 block">
                        Color de Tarjetas de Armazones
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={form.color_fondo_tarjetas}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, color_fondo_tarjetas: e.target.value }))
                          }
                          className="w-10 h-10 rounded-xl cursor-pointer border-0 p-0 bg-transparent"
                        />
                        <input
                          type="text"
                          value={form.color_fondo_tarjetas}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, color_fondo_tarjetas: e.target.value }))
                          }
                          className="flex-1 font-mono text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                        />
                      </div>
                    </div>

                    <div className="p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 space-y-3">
                      <label className="text-xs font-bold text-slate-700 dark:text-slate-200 block">
                        Color de Botones de Compra y Acción
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={form.color_botones}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, color_botones: e.target.value }))
                          }
                          className="w-10 h-10 rounded-xl cursor-pointer border-0 p-0 bg-transparent"
                        />
                        <input
                          type="text"
                          value={form.color_botones}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, color_botones: e.target.value }))
                          }
                          className="flex-1 font-mono text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                        />
                      </div>
                    </div>

                    <div className="p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 space-y-3">
                      <label className="text-xs font-bold text-slate-700 dark:text-slate-200 block">
                        Color de Texto en Botones
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={form.color_texto_botones}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, color_texto_botones: e.target.value }))
                          }
                          className="w-10 h-10 rounded-xl cursor-pointer border-0 p-0 bg-transparent"
                        />
                        <input
                          type="text"
                          value={form.color_texto_botones}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, color_texto_botones: e.target.value }))
                          }
                          className="flex-1 font-mono text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Vista Previa en Vivo */}
                  <div className="space-y-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block">
                      Vista Previa en Vivo de tu Tienda
                    </span>
                    <div
                      className="rounded-2xl p-5 border border-slate-200 dark:border-slate-700 shadow-inner transition-colors duration-300"
                      style={{ backgroundColor: form.color_fondo_principal }}
                    >
                      <div className="flex items-center justify-between pb-3 border-b border-white/10 mb-4">
                        <span className="text-sm font-black tracking-tight text-white">
                          {infoOptica?.nombre || 'H&L Óptica'}
                        </span>
                        <span
                          className="px-3 py-1 rounded-full text-[11px] font-bold flex items-center gap-1.5 shadow-sm"
                          style={{
                            backgroundColor: form.color_botones,
                            color: form.color_texto_botones
                          }}
                        >
                          <ShoppingBagIcon className="w-3.5 h-3.5" /> Carrito
                        </span>
                      </div>

                      {/* Mini Product Card Preview */}
                      <div
                        className="rounded-xl p-4 border border-white/10 flex items-center justify-between gap-3"
                        style={{ backgroundColor: form.color_fondo_tarjetas }}
                      >
                        <div>
                          <span className="text-[10px] uppercase font-bold text-cyan-400 block">
                            VULK • RECETA
                          </span>
                          <span className="text-sm font-bold text-white block">
                            Armazón Modelo Preview
                          </span>
                          <span className="text-base font-black text-emerald-400 block mt-1">
                            $118.500
                          </span>
                        </div>
                        <button
                          type="button"
                          className="px-3.5 py-2.5 rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-md"
                          style={{
                            backgroundColor: form.color_botones,
                            color: form.color_texto_botones
                          }}
                        >
                          <ShoppingBagIcon className="w-4 h-4" />
                          <span>Comprar</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2: NOTIFICACIONES POR EMAIL */}
              {tabActiva === 'email' && (
                <div className="space-y-6 animate-fadeIn">
                  {/* Correo Central de la Plataforma */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50/50 dark:from-slate-800/80 dark:to-indigo-950/30 border border-blue-200 dark:border-blue-900/60 rounded-2xl p-5 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-xl bg-blue-600 text-white shrink-0 mt-0.5">
                        <ShieldCheckIcon className="w-5 h-5" />
                      </div>
                      <div className="space-y-1">
                        <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                          Servicio de Correo Central de la Plataforma (Gmail)
                        </h4>
                        <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                          La plataforma incluye un servicio de correo automático que funciona de forma predeterminada para todas las ópticas.
                          Los correos emitidos llegarán con el nombre visible de tu óptica y cuando tus clientes respondan, sus mensajes llegarán directamente a tu casilla.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Casilla donde la óptica recibe alertas de compras */}
                  <div className="p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 space-y-2">
                    <label className="text-xs font-bold text-slate-700 dark:text-slate-200 block">
                      Email donde querés recibir las alertas de nuevas ventas:
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="p-2.5 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-400">
                        <AtSymbolIcon className="w-4 h-4" />
                      </div>
                      <input
                        type="email"
                        value={emailForm.email_notificaciones}
                        onChange={(e) =>
                          setEmailForm((prev) => ({ ...prev, email_notificaciones: e.target.value }))
                        }
                        placeholder="ejemplo@optica.com"
                        className="flex-1 text-xs px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                      Aquí te avisaremos por correo cada vez que un cliente complete un pedido en tu vidriera online.
                    </p>
                  </div>

                  {/* Toggle para usar cuenta propia */}
                  <div className="p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800/50 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <span className="text-xs font-bold text-slate-900 dark:text-white block">
                          Personalizar remitente con mi propio correo (SMTP / Gmail)
                        </span>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">
                          Opcional. Activalo si deseás que los emails salgan 100% desde tu propia casilla corporativa o de Google.
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={emailForm.usar_smtp_propio}
                          onChange={(e) =>
                            setEmailForm((prev) => ({ ...prev, usar_smtp_propio: e.target.checked }))
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>

                    {emailForm.usar_smtp_propio && (
                      <div className="pt-3 border-t border-slate-200 dark:border-slate-700/80 space-y-4 animate-fadeIn">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                              Servidor SMTP
                            </label>
                            <input
                              type="text"
                              value={emailForm.smtp_host}
                              onChange={(e) =>
                                setEmailForm((prev) => ({ ...prev, smtp_host: e.target.value }))
                              }
                              placeholder="smtp.gmail.com"
                              className="w-full text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                              Puerto
                            </label>
                            <input
                              type="number"
                              value={emailForm.smtp_port}
                              onChange={(e) =>
                                setEmailForm((prev) => ({ ...prev, smtp_port: e.target.value }))
                              }
                              placeholder="587"
                              className="w-full text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                              Usuario o Correo Remitente
                            </label>
                            <input
                              type="text"
                              value={emailForm.smtp_user}
                              onChange={(e) =>
                                setEmailForm((prev) => ({ ...prev, smtp_user: e.target.value }))
                              }
                              placeholder="tu_optica@gmail.com"
                              className="w-full text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-slate-700 dark:text-slate-300 flex items-center justify-between">
                              <span>Contraseña o App Password</span>
                              {emailForm.tiene_smtp_pass && (
                                <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                                  <CheckCircleSolidIcon className="w-3.5 h-3.5" /> Guardada cifrada
                                </span>
                              )}
                            </label>
                            <input
                              type="password"
                              value={emailForm.smtp_pass}
                              onChange={(e) =>
                                setEmailForm((prev) => ({ ...prev, smtp_pass: e.target.value }))
                              }
                              placeholder={emailForm.tiene_smtp_pass ? '••••••••' : 'Contraseña de aplicación de 16 caracteres'}
                              className="w-full text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                            />
                          </div>
                        </div>

                        <div className="flex items-start gap-2 text-[11px] text-slate-500 bg-slate-50 dark:bg-slate-900 p-3 rounded-xl border border-slate-200 dark:border-slate-700">
                          <InformationCircleIcon className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
                          <span>
                            Para cuentas de Gmail, debés generar una <strong>Contraseña de Aplicación</strong> de 16 dígitos desde tu cuenta Google (Seguridad &gt; Verificación en dos pasos &gt; Contraseñas de aplicación).
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Bloque de Prueba en Vivo */}
                  <div className="p-4 rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/20 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="space-y-0.5 text-center sm:text-left">
                      <span className="text-xs font-bold text-slate-800 dark:text-slate-200 block">
                        Enviar Correo de Prueba
                      </span>
                      <p className="text-[11px] text-slate-500">
                        Verificá al instante la recepción de emails transaccionales.
                      </p>
                    </div>

                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <input
                        type="email"
                        value={emailDestinoPrueba}
                        onChange={(e) => setEmailDestinoPrueba(e.target.value)}
                        placeholder="tu_email@ejemplo.com"
                        className="text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white w-full sm:w-56"
                      />
                      <button
                        type="button"
                        onClick={handleProbarEmail}
                        disabled={probandoEmail}
                        className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-slate-800 dark:bg-slate-700 hover:bg-slate-700 dark:hover:bg-slate-600 text-white text-xs font-bold transition-colors shrink-0 disabled:opacity-50"
                      >
                        {probandoEmail ? (
                          <ArrowPathIcon className="w-4 h-4 animate-spin" />
                        ) : (
                          <PaperAirplaneIcon className="w-4 h-4" />
                        )}
                        <span>Probar</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={guardando}
                className="flex items-center gap-1.5 px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl shadow-lg shadow-indigo-600/30 disabled:opacity-50 transition-colors"
              >
                {guardando ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    <span>Guardando...</span>
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="w-4 h-4" />
                    <span>Guardar Configuración</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

TiendaDisenoModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  tiendaData: PropTypes.object,
  storefrontUrl: PropTypes.string,
  onSaved: PropTypes.func
};
